import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Chip,
  useTheme,
  useMediaQuery,
  Paper,
  Stack,

  Fade,
  Zoom,

} from '@mui/material';
import HomeFooter from '../HomeFooter';
import Navbar from '../Navbar.jsx';

import { styled, alpha, keyframes } from '@mui/material/styles';
import {
  BugReport,
  Security,
  Speed,
  Api,
  Psychology,
  Code,
  Verified,
  TrendingUp,
  Dashboard,
  Shield,
  ArrowForward,
  WorkspacePremium,
  IntegrationInstructions,
  DeviceHub,
  CheckCircle,
  Email,
  Phone,
} from '@mui/icons-material';

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-20px); }
`;

// Professional color palette
const colors = {
  primary: '#4551be', // Blue accent
  secondary: '#6b77d4', // Lighter blue accent
  success: '#10b981', // Green
  warning: '#f59e0b', // Orange
  dark: '#1e293b', // Dark text
  light: '#f0f4f8', // Light background
  gray: '#64748b', // Slate 500
  accent: '#4551be',
  textSecondary: '#64748b', // Dark grey for secondary text
  gradient: 'linear-gradient(135deg, #4551be 0%, #6b77d4 100%)'
};

const ServiceCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: `0 20px 40px -12px ${alpha(colors.primary, 0.2)}`
  }
}));

const FeatureItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5)
}));

const SoftwareTestingServices = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [testProgress, setTestProgress] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setTestProgress(prev => prev >= 100 ? 0 : prev + 1);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  // Testing Services Data
  const testingServices = [
    {
      icon: <Psychology />,
      title: 'Manual Testing',
      description: 'Expert manual testing services combining human expertise with systematic testing methodologies to ensure comprehensive bug detection and usability validation.',
      color: colors.primary,
      features: ['Functional Testing', 'Usability Testing', 'Exploratory Testing', 'Regression Testing']
    },
    {
      icon: <Security />,
      title: 'Security Testing',
      description: 'Comprehensive security testing to identify vulnerabilities and protect against cyber threats through penetration testing and security audits.',
      color: colors.warning,
      features: ['Penetration Testing', 'Vulnerability Assessment', 'Security Audits', 'Threat Modeling']
    },
    {
      icon: <Speed />,
      title: 'Performance Testing',
      description: 'Performance testing services including load testing, stress testing and scalability analysis to ensure optimal application performance.',
      color: colors.success,
      features: ['Load Testing', 'Stress Testing', 'Scalability Testing', 'Endurance Testing']
    },
    {
      icon: <Api />,
      title: 'API Testing',
      description: 'Comprehensive API testing including functional, load, security testing to ensure your APIs work correctly and efficiently.',
      color: colors.secondary,
      features: ['REST API Testing', 'SOAP Testing', 'GraphQL Testing', 'Webhook Testing']
    },
    {
      icon: <DeviceHub />,
      title: 'Mobile App Testing',
      description: 'End-to-end testing of mobile applications across iOS and Android platforms to ensure functionality, usability and performance on various devices.',
      color: colors.success,
      features: ['Cross-platform Testing', 'Device Compatibility', 'Battery Consumption', 'Network Switching']
    },
    {
      icon: <BugReport />,
      title: 'Automation Testing',
      description: 'Automation testing services that improve testing speed, coverage and accuracy while reducing manual effort through automated test suites.',
      color: colors.success,
      features: ['Selenium Testing', 'Cypress Testing', 'Playwright Testing', 'Appium Testing']
    }
  ];

  // Testing Strategies
  const testingStrategies = [
    {
      title: 'Unit Testing',
      description: 'Testing individual components in isolation to ensure each unit functions correctly before integration.',
      icon: <Code />
    },
    {
      title: 'Integration Testing',
      description: 'Testing interactions between integrated components to verify they work together seamlessly.',
      icon: <IntegrationInstructions />
    },
    {
      title: 'System Testing',
      description: 'End-to-end testing of complete systems to validate the entire software against requirements.',
      icon: <DeviceHub />
    },
    {
      title: 'Acceptance Testing',
      description: 'Validating software with end-users to ensure it meets business requirements and is ready for deployment.',
      icon: <Verified />
    }
  ];

  // Why Choose Us
  const benefits = [
    {
      icon: <WorkspacePremium />,
      title: 'Expertise & Experience',
      description: 'Our team has 10+ years of software testing experience with proven methodologies and best practices.'
    },
    {
      icon: <Shield />,
      title: 'Quality Assurance',
      description: 'Comprehensive quality assurance services that guarantee bug-free, high-quality software delivery.'
    },
    {
      icon: <TrendingUp />,
      title: 'Cost Efficiency',
      description: 'Early bug detection reduces overall development costs by up to 40%.'
    },
    {
      icon: <Dashboard />,
      title: 'Comprehensive Coverage',
      description: 'End-to-end testing coverage across all stages of software development lifecycle.'
    }
  ];

  const process = [
    { step: "01", title: "Requirement Analysis", description: "We analyze your testing requirements and create a comprehensive test strategy" },
    { step: "02", title: "Test Planning", description: "We develop detailed test plans, test cases and testing schedules" },
    { step: "03", title: "Test Execution", description: "We execute test cases and document results systematically" },
    { step: "04", title: "Defect Tracking", description: "We track and manage defects through our robust bug tracking system" },
    { step: "05", title: "Reporting", description: "We provide detailed test reports and quality metrics" },
    { step: "06", title: "Test Closure", description: "We ensure all test objectives are met and deliver final test summary" }
  ];

  return (
    <Box sx={{ bgcolor: colors.light }}>
      <Navbar />

      {/* Hero Section */}
      <Box
  sx={{
    minHeight: { xs: 'auto', md: '60vh' },
    display: 'flex',
    alignItems: 'center',

    // Navbar overlap fix
   pt: { xs: 9, sm: 7, md: 8 },
    pb: { xs: 7, sm: 9, md: 12 },

    background: `linear-gradient(
      135deg,
      ${alpha(colors.primary, 0.03)} 0%,
      ${alpha(colors.secondary, 0.03)} 100%
    )`,

    position: 'relative',
    overflow: 'hidden',
    width: '100%',
  }}
>
        <Container maxWidth="xl" sx={{ position: 'relative' }}>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in timeout={1000}>
                <Box>
                  <Chip
                    icon={<BugReport />}
                    label="⚡ SOFTWARE TESTING EXPERTS"
                    sx={{
                      mb: 3,
                      mt: { xs: 2, md: 10 },
                      bgcolor: alpha(colors.primary, 0.1),
                      color: colors.primary,
                      fontWeight: 600,
                      border: `1px solid ${alpha(colors.primary, 0.2)}`
                    }}
                  />

                  {/* Title Changed to Match the Bower Cred Image Colour */}
                  <Typography variant="h1" sx={{
                    fontSize: { xs: '2.5rem', md: '3.5rem' },
                    fontWeight: 800,
                    lineHeight: 1.2,
                    mb: 3,
                    color: '#4551be', // <--- EXACT COLOUR FROM YOUR IMAGE
                  }}>
                    Software Testing
                    <Box component="span" sx={{
                      display: 'block',
                      color: '#4551be', // <--- EXACT COLOUR FROM YOUR IMAGE
                    }}>
                      Excellence
                    </Box>
                  </Typography>

                  <Typography sx={{
                    color: colors.textSecondary, // Dark grey text
                    mb: 4,
                    fontSize: '1.2rem',
                    lineHeight: 1.7,
                    maxWidth: '90%'
                  }}>
                    We provide comprehensive software testing services to ensure your applications are bug-free,
                    secure and perform optimally. Our testing solutions combine human expertise with advanced
                    technology to deliver quality results.
                  </Typography>

                </Box>
              </Fade>
            </Grid>

            <Grid item xs={12} md={6}>
              <Zoom in timeout={1000} style={{ transitionDelay: '300ms' }}>
                <Box sx={{
                  animation: `${float} 6s ease-in-out infinite`,
                  textAlign: 'center',
                  position: 'relative',
                  width: '100%',
                  maxWidth: 500,
                  mx: 'auto',
                  py: { xs: 4, md: 2 },
                }}>
                  {/* Main blob */}
                  <Box sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: { xs: '240px', sm: '300px', md: '360px' },
                    height: { xs: '240px', sm: '300px', md: '360px' },
                    borderRadius: '30% 70% 70% 30% / 30% 30% 70% 70%',
                    background: `linear-gradient(135deg, ${alpha(colors.primary, 0.1)} 0%, ${alpha(colors.secondary, 0.1)} 100%)`,
                    position: 'relative',
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: -15,
                      left: -15,
                      right: -15,
                      bottom: -15,
                      borderRadius: '40% 60% 60% 40% / 40% 40% 60% 60%',
                      background: `linear-gradient(135deg, ${alpha(colors.primary, 0.2)} 0%, ${alpha(colors.secondary, 0.2)} 100%)`,
                      zIndex: -1,
                      animation: `${float} 8s ease-in-out infinite reverse`
                    }
                  }}>
                    <BugReport sx={{
                      fontSize: { xs: 80, sm: 120, md: 150 },
                      color: colors.primary
                    }} />
                  </Box>

                  {/* Testing Progress Indicator — INSIDE container bounds */}
                  <Box sx={{
                    position: 'absolute',
                    top: { xs: '0%', md: '15%' },
                    right: { xs: '0%', md: '5%' },
                    width: { xs: 65, md: 80 },
                    height: { xs: 65, md: 80 },
                    borderRadius: '50%',
                    background: alpha(colors.success, 0.2),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: `${float} 7s ease-in-out infinite`,
                    animationDelay: '1s',
                    zIndex: 2,
                  }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" sx={{
                        color: colors.success,
                        fontWeight: 700,
                        fontSize: { xs: '1rem', md: '1.25rem' }
                      }}>
                        {testProgress}%
                      </Typography>
                      <Typography variant="caption" sx={{
                        color: colors.success,
                        fontSize: { xs: '0.6rem', md: '0.75rem' }
                      }}>
                        Testing
                      </Typography>
                    </Box>
                  </Box>

                  {/* Shield badge — bottom-left, inside bounds */}
                  <Box sx={{
                    position: 'absolute',
                    bottom: { xs: '5%', md: '20%' },
                    left: { xs: '0%', md: '5%' },
                    width: 60,
                    height: 60,
                    borderRadius: '10px',
                    background: alpha(colors.warning, 0.2),
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: `${float} 8s ease-in-out infinite`,
                    animationDelay: '2s',
                    zIndex: 2,
                  }}>
                    <Security sx={{ color: colors.warning }} />
                  </Box>

                  {/* Speed badge — top-left, inside bounds */}
                  <Box sx={{
                    position: 'absolute',
                    top: { xs: '5%', md: '30%' },
                    left: { xs: '0%', md: '15%' },
                    width: 45,
                    height: 45,
                    borderRadius: '50%',
                    background: alpha(colors.secondary, 0.2),
                    display: { xs: 'none', md: 'flex' },
                    alignItems: 'center',
                    justifyContent: 'center',
                    animation: `${float} 9s ease-in-out infinite`,
                    animationDelay: '1.5s',
                    zIndex: 2,
                  }}>
                    <Speed sx={{ color: colors.secondary, fontSize: 25 }} />
                  </Box>
                </Box>
              </Zoom>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Process Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center" mb={6}>
          <Chip
            label="OUR TESTING PROCESS"
            sx={{
              bgcolor: alpha(colors.primary, 0.1),
              color: colors.primary,
              fontWeight: 600,
              mb: 2
            }}
          />
          <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: colors.dark }}>
            How We Test Your Software
          </Typography>
          <Typography variant="h5" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto', color: colors.textSecondary }}>
            A systematic approach to ensure comprehensive test coverage
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {process.map((item, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Paper sx={{
                p: 3,
                height: '100%',
                position: 'relative',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 12px 24px ${alpha(colors.primary, 0.15)}`
                }
              }}>
                <Typography variant="h2" sx={{
                  position: 'absolute',
                  top: 10,
                  right: 20,
                  fontSize: '4rem',
                  fontWeight: 900,
                  color: alpha(colors.primary, 0.1),
                  lineHeight: 1
                }}>
                  {item.step}
                </Typography>
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                  <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: colors.primary }}>
                    {item.title}
                  </Typography>
                  <Typography color="text.secondary" sx={{ color: colors.textSecondary }}>
                    {item.description}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Testing Services */}
      <Box sx={{ bgcolor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Chip
              label="OUR TESTING SERVICES"
              sx={{
                bgcolor: alpha(colors.primary, 0.1),
                color: colors.primary,
                fontWeight: 600,
                mb: 2
              }}
            />
            <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: colors.dark }}>
              Comprehensive Testing Solutions
            </Typography>
            <Typography variant="h5" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto', color: colors.textSecondary }}>
              We provide end-to-end testing services to ensure your software meets the highest quality standards
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {testingServices.map((service, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <ServiceCard>
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{
                      width: 50,
                      height: 50,
                      borderRadius: 2,
                      background: alpha(service.color, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      color: service.color
                    }}>
                      {service.icon}
                    </Box>

                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: colors.dark }}>
                      {service.title}
                    </Typography>

                    <Typography color="text.secondary" paragraph sx={{ color: colors.textSecondary }}>
                      {service.description}
                    </Typography>

                    <Stack spacing={1} sx={{ mt: 2 }}>
                      {service.features.map((feature, idx) => (
                        <FeatureItem key={idx}>
                          <CheckCircle sx={{ fontSize: 16, color: colors.success }} />
                          <Typography variant="body2" sx={{ color: colors.dark }}>{feature}</Typography>
                        </FeatureItem>
                      ))}
                    </Stack>
                  </CardContent>
                </ServiceCard>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Testing Strategies */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box textAlign="center" mb={6}>
          <Chip
            label="TESTING STRATEGIES"
            sx={{
              bgcolor: alpha(colors.primary, 0.1),
              color: colors.primary,
              fontWeight: 600,
              mb: 2
            }}
          />
          <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: colors.dark }}>
            Our Testing Approach
          </Typography>
        </Box>

        <Grid container spacing={3}>
          {testingStrategies.map((strategy, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Paper sx={{ p: 3, height: '100%' }}>
                <Box sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  background: colors.gradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                  color: 'white'
                }}>
                  {strategy.icon}
                </Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: colors.dark }}>
                  {strategy.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ color: colors.textSecondary }}>
                  {strategy.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Why Choose Us */}
      <Box sx={{ bgcolor: 'white', py: 8 }}>
        <Container maxWidth="lg">
          <Box textAlign="center" mb={6}>
            <Chip
              label="WHY CHOOSE US"
              sx={{
                bgcolor: alpha(colors.primary, 0.1),
                color: colors.primary,
                fontWeight: 600,
                mb: 2
              }}
            />
            <Typography variant="h2" sx={{ fontWeight: 700, mb: 2, color: colors.dark }}>
              Why Leading Companies Trust Us
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper sx={{
                  p: 3,
                  height: '100%',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 24px ${alpha(colors.primary, 0.15)}`
                  }
                }}>
                  <Box sx={{
                    width: 48,
                    height: 48,
                    borderRadius: 2,
                    background: colors.gradient,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2,
                    color: 'white'
                  }}>
                    {benefit.icon}
                  </Box>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: colors.dark }}>
                    {benefit.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ color: colors.textSecondary }}>
                    {benefit.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>

        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          background: colors.gradient,
          py: { xs: 5, md: 8 },
          px: 2,
        }}
      >
        <Container maxWidth="md" sx={{ textAlign: "center" }}>
          <Typography
            variant="h3"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: "#ffffff",
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
            }}
          >
            Ready to Ensure Software Quality?
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: alpha("#ffffff", 0.9),
              mb: 4,
              fontSize: { xs: "1rem", md: "1.25rem" },
            }}
          >
            Join 500+ companies that trust us with their software testing needs.
          </Typography>

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "center",
              alignItems: "center",
              gap: { xs: 2, sm: 4 },
              mt: 4,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Email sx={{ color: "#ffffff" }} />

              <Typography
                sx={{
                  color: "#ffffff",
                  fontSize: { xs: "0.9rem", md: "1rem" },
                  wordBreak: "break-word",
                }}
              >
                lidertechnologysolutions@gmail.com
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <Phone sx={{ color: "#ffffff" }} />

              <Typography
                sx={{
                  color: "#ffffff",
                  fontSize: { xs: "0.9rem", md: "1rem" },
                }}
              >
                +91-98403 69993
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <HomeFooter />
    </Box>
  );
};

export default SoftwareTestingServices;